import { assetStrategyDocument, readStrategyPosition, writeStrategyPosition } from '../graphql/assetStrategies';
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import toast from 'react-hot-toast';
import { GET_EXIT_STRATEGY_TEMPLATES } from '../graphql/queries/strategyTemplates';
import { REMOVE_EXIT_STRATEGY } from '../graphql/mutations/exitStrategy';
import ExitStrategyPlanner from './ExitStrategyPlanner';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { DISTRIBUTION_CURVES } from '../utils/distributionCurves';

const CUSTOM_STRATEGY = '__custom__';

// Convert backend distributionCurve to frontend format
const convertDistributionCurve = (backendValue) => {
  if (!backendValue) return undefined;
  const mapping = {
    'BELL': DISTRIBUTION_CURVES.BELL,
    'FRONT_LOADED': DISTRIBUTION_CURVES.FRONT_LOADED,
    'BACK_LOADED': DISTRIBUTION_CURVES.BACK_LOADED,
    'VALLEY': DISTRIBUTION_CURVES.VALLEY,
  };
  return mapping[backendValue] || backendValue;
};

// Convert backend baseSource to frontend format
const convertBaseSource = (backendValue) => {
  if (!backendValue) return undefined;
  const mapping = {
    'AVG': 'avg',
    'CURRENT': 'current',
  };
  return mapping[backendValue] || backendValue.toLowerCase();
};

// Convert saved params from API to frontend format
const convertSavedParams = (params) => {
  if (!params) return null;
  return {
    targetMultiple: params.targetMultiple,
    baseSource: convertBaseSource(params.baseSource),
    exitLevelsCount: params.exitLevelsCount,
    exitLevelsSpread: params.exitLevelsSpread,
    takeInitialsEnabled: params.takeInitialsEnabled,
    takeInitialsMultiple: params.takeInitialsMultiple,
    hodlEnabled: params.hodlEnabled,
    hodlPercentage: params.hodlPercentage,
    advancedDistributionEnabled: params.advancedDistributionEnabled,
    distributionCurve: convertDistributionCurve(params.distributionCurve),
    distributionFocus: params.distributionFocus,
    distributionIntensity: params.distributionIntensity
  };
};

const ExitStrategyModal = ({ isOpen, onClose, position, token, portfolioId, updateCache, refetch, assetKind = 'crypto' }) => {
  const [selectedStrategyId, setSelectedStrategyId] = useState(CUSTOM_STRATEGY);
  const [templateValues, setTemplateValues] = useState(null);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);

  // Load templates
  const { data: templatesData, loading: templatesLoading } = useQuery(GET_EXIT_STRATEGY_TEMPLATES, {
    skip: !isOpen
  });

  const [removeExitStrategy] = useMutation(assetStrategyDocument(REMOVE_EXIT_STRATEGY, assetKind));
  const templates = useMemo(() => {
    return templatesData?.exitStrategyTemplates || [];
  }, [templatesData?.exitStrategyTemplates]);
  const hasTemplates = templates.length > 0;

  // Track if form has been modified from template
  const [formModified, setFormModified] = useState(false);

  // Position info
  const hasExitStrategy = position?.exitStrategy?.levels?.length > 0;

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Prevent background scroll and allow modal content scroll
  useEffect(() => {
    if (isOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original || '';
      };
    }
  }, [isOpen]);

  // Initialize dropdown and template values based on existing position's template or saved params
  useEffect(() => {
    if (!isOpen || templatesLoading) return;
    
    const existingTemplateId = position?.exitStrategy?.templateId;
    const hasExistingLevels = position?.exitStrategy?.levels?.length > 0;
    const savedParams = position?.exitStrategy?.params;
    
    if (existingTemplateId && templates.length > 0) {
      // Check if the template still exists in our list
      const template = templates.find(t => t.id === existingTemplateId);
      if (template) {
        setSelectedStrategyId(existingTemplateId);
        // Populate template values so the form shows current template settings
        setTemplateValues({
          id: template.id,
          targetMultiple: template.targetMultiple,
          baseSource: convertBaseSource(template.baseSource),
          exitLevelsCount: template.exitLevelsCount,
          exitLevelsSpread: template.exitLevelsSpread,
          takeInitialsEnabled: template.takeInitialsEnabled,
          takeInitialsMultiple: template.takeInitialsMultiple,
          hodlEnabled: template.hodlEnabled,
          hodlPercentage: template.hodlPercentage,
          advancedDistributionEnabled: template.advancedDistributionEnabled,
          distributionCurve: convertDistributionCurve(template.distributionCurve),
          distributionFocus: template.distributionFocus,
          distributionIntensity: template.distributionIntensity
        });
        return;
      }
    }
    
    // For custom strategies (no template), use saved params if available
    if (hasExistingLevels && savedParams) {
      const convertedParams = convertSavedParams(savedParams);
      if (convertedParams) {
        setSelectedStrategyId(CUSTOM_STRATEGY);
        setTemplateValues(convertedParams);
      }
    }
  }, [isOpen, templatesLoading, templates, position?.exitStrategy?.templateId, position?.exitStrategy?.levels, position?.exitStrategy?.params]);

  // When a template is selected, store its values
  const handleStrategySelect = (strategyId) => {
    setSelectedStrategyId(strategyId);
    setFormModified(false);
    
    if (strategyId !== CUSTOM_STRATEGY) {
      const template = templates.find(t => t.id === strategyId);
      if (template) {
        setTemplateValues({
          id: template.id, // Required for change detection in planner
          targetMultiple: template.targetMultiple,
          baseSource: convertBaseSource(template.baseSource),
          exitLevelsCount: template.exitLevelsCount,
          exitLevelsSpread: template.exitLevelsSpread,
          takeInitialsEnabled: template.takeInitialsEnabled,
          takeInitialsMultiple: template.takeInitialsMultiple,
          hodlEnabled: template.hodlEnabled,
          hodlPercentage: template.hodlPercentage,
          advancedDistributionEnabled: template.advancedDistributionEnabled,
          distributionCurve: convertDistributionCurve(template.distributionCurve),
          distributionFocus: template.distributionFocus,
          distributionIntensity: template.distributionIntensity
        });
      }
    } else {
      setTemplateValues(null);
    }
  };

  // Handle form modification callback from planner
  const handleFormModified = () => {
    if (selectedStrategyId !== CUSTOM_STRATEGY && !formModified) {
      setFormModified(true);
      setSelectedStrategyId(CUSTOM_STRATEGY);
      setTemplateValues(null);
    }
  };

  // Handle remove exit strategy
  const handleRemove = async () => {
    if (removing) return;
    setRemoving(true);
    try {
      const { data } = await removeExitStrategy({
        variables: { portfolioId, tokenId: token.id },
        update: (cache, { data }) => {
          if (!data?.removeExitStrategy?.success) return;
          try {
            const existing = readStrategyPosition(cache, assetKind, token, portfolioId);
            if (existing?.tokenWithPosition?.position) {
              const updated = {
                ...existing,
                tokenWithPosition: {
                  ...existing.tokenWithPosition,
                  position: {
                    ...existing.tokenWithPosition.position,
                    exitStrategy: null,
                    planned: null,
                  }
                },
              };
              writeStrategyPosition(cache, assetKind, token, portfolioId, updated);
            }
          } catch (_) { /* ignore */ }
        },
      });
      if (data?.removeExitStrategy?.success) {
        toast.success('Exit strategy removed');
        setShowRemoveConfirm(false);
        onClose();
        if (typeof refetch === 'function') {
          try { refetch(); } catch (_) {}
        }
      } else {
        toast.error(data?.removeExitStrategy?.error || 'Failed to remove exit strategy');
      }
    } catch (err) {
      toast.error('Failed to remove exit strategy');
    } finally {
      setRemoving(false);
    }
  };

  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto overscroll-contain p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full relative max-w-xl md:max-w-4xl lg:max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {hasExitStrategy ? 'Edit' : 'Set'} Exit Strategy
              {token?.name && <span className="text-gray-500 font-normal ml-2">for {token.name}</span>}
            </h2>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Strategy dropdown */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Strategy
              </label>
              <Link 
                to="/strategies?tab=exit" 
                className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                Edit strategies →
              </Link>
            </div>
            {templatesLoading ? (
              <div className="text-center py-2 text-gray-500">Loading strategies...</div>
            ) : (
              <select
                value={selectedStrategyId}
                onChange={(e) => handleStrategySelect(e.target.value)}
                disabled={!hasTemplates}
                className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  !hasTemplates ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                <option value={CUSTOM_STRATEGY}>Custom strategy</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name} ({template.targetMultiple}x target, {template.exitLevelsCount} levels)
                  </option>
                ))}
              </select>
            )}
            {!hasTemplates && !templatesLoading && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                No saved strategies available. Configure custom settings below.
              </p>
            )}
          </div>

          {/* Exit Strategy Planner */}
          <ExitStrategyPlanner assetKind={assetKind}
            position={position}
            token={token}
            averageCost={position?.averageBuyPrice}
            holdingsAmount={position?.holdingsAmount}
            portfolioId={portfolioId}
            updateCache={updateCache}
            refetch={refetch}
            onClose={onClose}
            templateValues={templateValues}
            onFormModified={handleFormModified}
            selectedTemplateId={selectedStrategyId !== CUSTOM_STRATEGY ? selectedStrategyId : null}
            onRemove={hasExitStrategy ? () => setShowRemoveConfirm(true) : null}
          />
        </div>
      </div>

      {showRemoveConfirm && (
        <ConfirmDeleteModal
          title="Remove Exit Strategy"
          message="Are you sure you want to remove the exit strategy from this position? All exit levels will be deleted."
          onConfirm={handleRemove}
          onCancel={() => setShowRemoveConfirm(false)}
        />
      )}
    </div>
  );
};

export default ExitStrategyModal;
